import { modellAusKurzform, ohneFable } from "../server/models.js";
import { claudeName } from "./modell-namen.js";
/**
 * voice-v8 — Die Modelle, die im Max-Abo antworten (30.08.2026)
 *
 * Martin: *„Kann man da Fable auch mit einbauen?"*
 *
 * Man kann — nachgemessen auf der CLI, bevor hier etwas entstand:
 *
 *   claude -p --model fable "Antworte NUR mit: ok"        -> ok
 *   claude -p --model claude-fable-5 --effort max ...      -> ok
 *   ... --model fable --allowedTools Read "Lies probe.txt" -> Inhalt der Datei
 *
 * Die dritte Zeile war die wichtige: Fable wird bei uns bisher NUR fuer
 * Fliesstext benutzt (Buch, Essenzspiegel, Projektbericht) und dort
 * ausdruecklich ohne Werkzeuge (`TEXT_ONLY_DISALLOWED` in strong-model.ts).
 * Im Chat sitzt es dagegen in einer Sitzung voller Werkzeuge — Kalender,
 * Post, Gedaechtnis. Dass es die auch bedient, war eine Annahme, bis die
 * Probe die Datei wirklich gelesen hat.
 *
 * ## Warum diese Liste hier steht und nicht viermal
 *
 * ⚠️ Vor dieser Datei stand dieselbe Aufzaehlung an FUENF Stellen:
 * `frontend/lib/settings.ts` (Liste + Typ + Einleseschutz),
 * `server/modell-einstellungen.ts`, `server/settings-sanitize.ts`,
 * `server/chat.ts` und die Etiketten in `frontend/app.ts`. Am 27.08. wurde
 * Haiku ergaenzt — und fiel in der Fusszeile still in den Sonnet-Zweig, weil
 * eine der Stellen vergessen wurde. Am 30.08. wiederholte sich dasselbe bei
 * der Selbstauskunft.
 *
 * Ein neues Modell ist ab jetzt EIN Eintrag hier. Wer eine sechste Stelle
 * anlegt, holt den Fehler zurueck.
 *
 * ⚠️⚠️ 23.09.2026, Ticket 2516 — Fable ist wieder draußen. Martin: *„Fable
 *    5.1 fällt bis auf Weiteres aus dem System."* Der Eintrag ist weg, und
 *    genau wie oben versprochen ist das EINE Stelle: Chip, Fußzeile und
 *    Einstellungen folgen von selbst. Wer „fable" gespeichert hat, landet
 *    NICHT still bei „auto" (= meist Sonnet), sondern bei Opus — siehe
 *    {@link aboWahlAus}.
 */

export const ABO_MODELLE = [
  {
    id: "auto",
    label: "Automatisch",
    description:
      "Sonnet — und Opus, wenn es um E-Mails, Analysen oder Rechnungen geht",
  },
  { id: "sonnet", label: "Sonnet", description: "Schnell, gut für den Alltag" },
  {
    id: "opus",
    label: "Opus",
    description: "Stärkstes Modell — langsamer, gründlicher",
  },
  { id: "haiku", label: "Haiku", description: "Am schnellsten, für Kurzes" },
] as const;

/** Was am Modell-Chip stehen kann — „auto" eingeschlossen. */
export type AboWahl = (typeof ABO_MODELLE)[number]["id"];

/** Was tatsaechlich antwortet — „auto" ist dann schon aufgeloest. */
export type AboLauf = Exclude<AboWahl, "auto">;

const IDS = new Set<string>(ABO_MODELLE.map((m) => m.id));

/**
 * Ist das eine gueltige Wahl?
 *
 * ⚠️ Streng: kein Trimmen, keine Grossschreibung, keine Teiltreffer. Was
 * nicht exakt passt, ist kein Modell — der Aufrufer faellt dann auf „auto"
 * zurueck, statt etwas Erfundenes weiterzureichen.
 */
export function istAboWahl(x: unknown): x is AboWahl {
  return typeof x === "string" && IDS.has(x);
}

/**
 * Eine GESPEICHERTE Wahl lesen. (23.09.2026, Ticket 2516)
 *
 * ⚠️ Nicht dasselbe wie {@link istAboWahl}: die prüft streng, was angeboten
 *    wird. Hier geht es um das, was schon in users.settings oder im
 *    Browser liegt — und dort kann noch „fable" stehen. Das wird Opus, nicht
 *    „auto": wer das erzählende Modell fest gewählt hatte, wollte das
 *    stärkste, nicht die Automatik, die meist Sonnet fährt.
 */
export function aboWahlAus(x: unknown): AboWahl {
  const w = ohneFable(x);
  return istAboWahl(w) ? w : "auto";
}

/**
 * Was die tiefen Auswertungen fahren dürfen — Spiegel, Buch, Hörfassung,
 * Bericht. (23.09.2026, Ticket 2516)
 *
 * ⚠️ Stand bis heute als `"fable" | "opus" | "sonnet"` an fünf Stellen
 *    (Server-Leser, Filter, Einstellungsseite, Start-Abgleich, Typ). Jetzt
 *    hier, einmal — dieselbe Lehre wie oben bei ABO_MODELLE.
 */
export const STARKE_MODELLE = ["opus", "sonnet"] as const;
export type StarkesModell = (typeof STARKE_MODELLE)[number];

/**
 * Eine gespeicherte Wahl für die tiefen Auswertungen lesen: „fable" wird
 * Opus, Unbekanntes wird `null` — der Aufrufer setzt dann SEINE Vorgabe.
 */
export function starkesModellAus(x: unknown): StarkesModell | null {
  const w = ohneFable(x);
  return STARKE_MODELLE.find((m) => m === w) ?? null;
}

/**
 * Welches Modell antwortet in DIESEM Zug?
 *
 * @param wahl   Was am Modell-Chip festgelegt ist.
 * @param schwer `forceHeavy || wantsOpusFor(text)` — zaehlt NUR bei „auto".
 *
 * ⚠️ 2026-08-30 (Martin: *„ich habe hier Opus eingestellt, aber im System
 * steht immer irgend etwas anderes"*): Diese Entscheidung stand zweimal in
 * `server/chat.ts` — einmal fuer das, was wirklich laeuft, und einmal fuer
 * das, was der System-Text ueber das Modell behauptet. Nur die erste kannte
 * die Wahl. Der Zug lief auf Opus, und der Bot nannte trotzdem Sonnet, weil
 * `wantsOpusFor("welches Modell bist du?")` nicht zutrifft.
 *
 * Eine Wahl, die wirkt, aber falsch ueber sich Auskunft gibt, ist schlimmer
 * als eine, die gar nicht wirkt: man kann sie nicht nachpruefen.
 */
export function aboModellFuer(wahl: AboWahl, schwer: boolean): AboLauf {
  if (wahl !== "auto") return wahl;
  return schwer ? "opus" : "sonnet";
}

/**
 * Das Schild in der Fusszeile fuer die warme Abo-Sitzung.
 *
 * ⚠️ Vorher standen hier drei fast gleiche Zweige — `warm-opus`, `warm-haiku`
 * und ein Auffang, der ALLES uebrige „Sonnet · Abo" nannte. Genau daran ist
 * Haiku am 27.08. haengengeblieben: ein neues Modell braucht keinen eigenen
 * Zweig mehr, sondern faellt still in den letzten. Jetzt entscheidet die
 * Liste aus dieser Datei; ohne Zusatz bleibt es Sonnet (so heissen
 * die alten gespeicherten Werte).
 */
export function warmEtikett(model: string): { label: string; titel: string } | null {
  const m = /claude-cli-warm(?:-([a-z0-9.-]+))?/i.exec(model);
  if (!m) return null;
  const id = (m[1] ?? "sonnet").toLowerCase();
  const eintrag = ABO_MODELLE.find((e) => e.id === id);
  if (!eintrag || eintrag.id === "auto") return null;
  const name = aboAnzeige(eintrag.id);
  return {
    label: `${name} · Abo`,
    titel: `${name} über dein Max-Abo · ${eintrag.description}`,
  };
}

/**
 * Der Name MIT Version, den Martin sieht: „Opus 5.5", „Sonnet 5".
 * (23.09.2026)
 *
 * ⚠️ Aus dem Register abgeleitet (server/models.ts), nicht in ABO_MODELLE
 *    hingeschrieben. Die Etiketten dort bleiben ohne Zahl, weil sie die
 *    WAHL bezeichnen („Opus"), nicht das Modell, das gerade dahinter steht.
 *    Welches das ist, entscheidet das Register — und beim nächsten Wechsel
 *    folgt diese Anzeige von selbst.
 */
export function aboAnzeige(id: string): string {
  const eintrag = ABO_MODELLE.find((e) => e.id === id);
  if (!eintrag || eintrag.id === "auto") return eintrag?.label ?? id;
  return claudeName(modellAusKurzform(eintrag.id)) || eintrag.label;
}

